import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount, useWriteContract, usePublicClient } from "wagmi";
import { useAppKit } from "@reown/appkit/react";
import { toast } from "sonner";
import { HandCoins, Loader2, RefreshCw } from "lucide-react";
import { getOnchainFees } from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { HOOK_ABI, BASE_CHAIN_ID } from "@/config/contracts";
import { formatUsd, shortAddr } from "@/lib/format";

const fmtAmt = (n) => {
  const v = Number(n || 0);
  return v >= 1 ? v.toLocaleString(undefined, { maximumFractionDigits: 4 }) : v.toPrecision(4);
};

// Creator fee claim panel: shows the accrued 60% swap-tax and lets the
// registered creator pull it on-chain via hook.claimCreatorFees(poolId).
export default function ClaimFees({ coin }) {
  const { address, isConnected } = useAccount();
  const { open } = useAppKit();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();
  const { hook } = useConfig();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const { data: fees, refetch } = useQuery({
    queryKey: ["fees", coin?.token_address],
    queryFn: () => getOnchainFees(coin.token_address),
    enabled: !!coin?.token_address,
    retry: false,
    refetchInterval: 20000,
  });

  const isCreator =
    address && fees?.creator && address.toLowerCase() === fees.creator.toLowerCase();
  if (!fees || !isCreator || !fees.claimable) return null;

  const claim = async () => {
    if (!isConnected) return open();
    if (!hook) return toast.error("Hook not configured");
    setBusy(true);
    try {
      const hash = await writeContractAsync({
        address: hook,
        abi: HOOK_ABI,
        functionName: "claimCreatorFees",
        args: [fees.pool_id],
        chainId: BASE_CHAIN_ID,
      });
      await publicClient.waitForTransactionReceipt({ hash });
      toast.success("Fees claimed", {
        description: `${fmtAmt(fees.creator_amount)} ${fees.fee_symbol} sent to ${shortAddr(fees.creator)}`,
      });
      qc.invalidateQueries({ queryKey: ["fees", coin.token_address] });
      refetch();
    } catch (e) {
      toast.error("Claim failed", { description: e?.shortMessage || e?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="claim-fees-panel"
      className="rounded-2xl border border-[#00e699]/25 bg-gradient-to-b from-[#00e699]/10 to-transparent p-4 sm:p-5"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="rounded-lg bg-[#00e699]/15 p-2 text-[#00e699]">
            <HandCoins size={16} />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-100">Creator fees</div>
            <div className="text-[11px] text-slate-500">60% of every swap tax</div>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          data-testid="claim-fees-refresh"
          className="rounded-lg p-1.5 text-slate-500 transition-colors hover:text-white"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      <div data-testid="claim-fees-amount" className="mt-3 font-mono2 text-2xl font-extrabold text-[#00e699]">
        {fmtAmt(fees.creator_amount)}{" "}
        <span className="text-sm font-bold text-slate-300">{fees.fee_symbol}</span>
      </div>
      {fees.creator_usd > 0 && <div className="text-xs text-slate-500">≈ {formatUsd(fees.creator_usd)}</div>}

      <button
        data-testid="claim-fees-button"
        onClick={claim}
        disabled={busy}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#00e699] py-3 text-sm font-bold text-[#052015] transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <HandCoins size={16} />}
        {busy ? "Claiming…" : `Claim ${fmtAmt(fees.creator_amount)} ${fees.fee_symbol}`}
      </button>
      <p className="mt-2 text-center text-[10px] text-slate-500">
        Paid to your creator wallet {shortAddr(fees.creator)}
      </p>
    </div>
  );
}
